import { MenuWrapper, useMenuContext } from './MenuWrapper';
// Support → /live-chat (`menu.liveChat` in locales).
import { NavItemButton } from '@/components/ui/navigation/NavItemButton';
import {
  getMoreMenuItemsForUser,
  getMoreMenuPaths,
  type BadgeState,
  type NavItem,
} from '@/lib/navigationItems';
import { isNative } from '@/lib/platform';
import { useNotificationSettingsListener } from '@/hooks/useNotificationSettingsListener';
import { useLocation } from 'react-router-dom';
import { isCurrentUserAdmin } from '@/hooks/useAdminMode';
import { useAuthState } from '@/hooks/useAuth';
import { isAdminUserEmail } from '@/components/admin/AdminAuthGuard';

const UTILITY_MORE_MENU_IDS = new Set(['liveChat', 'settings']);
const UTILITY_MORE_MENU_ORDER = ['liveChat', 'settings'] as const;

function sortUtilityMenuItems(items: NavItem[]): NavItem[] {
  return [...items].sort(
    (a, b) =>
      UTILITY_MORE_MENU_ORDER.indexOf(a.id as (typeof UTILITY_MORE_MENU_ORDER)[number]) -
      UTILITY_MORE_MENU_ORDER.indexOf(b.id as (typeof UTILITY_MORE_MENU_ORDER)[number]),
  );
}

interface UsersMenuProps {
  needsNotificationSetup?: boolean;
}

function UsersMenuSection({
  items,
  needsNotificationSetup,
  variant = 'primary',
}: UsersMenuProps & {
  items: NavItem[];
  variant?: 'primary' | 'utility';
}) {
  const { closeMenu } = useMenuContext();
  const { needsSetup: settingsNeedsSetup } = useNotificationSettingsListener();
  const location = useLocation();
  const isAdmin = isCurrentUserAdmin();

  const badgeState: BadgeState = {
    needsNotificationSetup: needsNotificationSetup || settingsNeedsSetup,
    isNative: isNative(),
    currentPath: location.pathname,
  };

  return (
    <>
      {items.map((item) => {
        let itemToRender = item;
        if (item.id === 'liveChat' && isAdmin) {
          itemToRender = {
            ...item,
            path: '/live-chat-admin',
            pathMatches: ['/live-chat-admin'],
          } as unknown as typeof item;
        }

        return (
          <div key={item.id} className={variant === 'utility' ? 'contents' : undefined}>
            <NavItemButton
              item={itemToRender}
              badgeState={badgeState}
              onNavigate={closeMenu}
              variant={variant === 'utility' ? 'utility' : 'default'}
            />
          </div>
        );
      })}
    </>
  );
}

export function UsersMenu({ needsNotificationSetup }: UsersMenuProps) {
  const { user } = useAuthState();
  const isAdmin = isAdminUserEmail(user?.email);
  const visible = getMoreMenuItemsForUser({ isArtist: false, isAdmin });
  const menuPaths = getMoreMenuPaths(visible);
  const utilityItems = sortUtilityMenuItems(
    visible.filter((item) => UTILITY_MORE_MENU_IDS.has(item.id)),
  );
  const bodyItems = visible.filter((item) => !UTILITY_MORE_MENU_IDS.has(item.id));

  return (
    <MenuWrapper
      needsNotificationSetup={needsNotificationSetup}
      menuPaths={menuPaths}
      utilityItems={
        utilityItems.length > 0 ? (
          <UsersMenuSection
            items={utilityItems}
            needsNotificationSetup={needsNotificationSetup}
            variant="utility"
          />
        ) : null
      }
    >
      <UsersMenuSection items={bodyItems} needsNotificationSetup={needsNotificationSetup} />
    </MenuWrapper>
  );
}
